import Image from "next/image"
import Link from "next/link"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

export interface AddOnCardProps {
  title: string
  description: string
  price: string
  /** Optional member price shown in green after the regular price
   *  (e.g. "CHF 139"). Rendered as "· CHF 139 {memberLabel}". */
  memberPrice?: string
  /** Translated suffix after the member price (e.g. "for members"). */
  memberLabel?: string
  imageSrc?: string
  href?: string
  /** Optional status pill rendered inline next to the title (e.g. the
   *  CMS-driven "Not available" pill for a service `not_available` in-market). */
  badge?: ReactNode
  className?: string
}

export function AddOnCard({
  title,
  description,
  price,
  memberPrice,
  memberLabel,
  imageSrc,
  href,
  badge,
  className,
}: AddOnCardProps) {
  const content = (
    <>
      {imageSrc && (
        <Image
          src={imageSrc}
          alt={title}
          width={64}
          height={64}
          className="flex-shrink-0 rounded-[12px] object-cover"
        />
      )}
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="type-title-md text-neutral-900">{title}</p>
          {badge}
        </div>
        <p className="type-body-md text-neutral-500">{description}</p>
        <p className="type-title-sm mt-1 text-neutral-900">
          {price}
          {memberPrice && (
            <span className="text-deep-green-500 font-normal">
              {" · "}
              {memberPrice}
              {memberLabel ? ` ${memberLabel}` : ""}
            </span>
          )}
        </p>
      </div>
    </>
  )

  const styles = cn(
    "flex items-start gap-4 p-4 bg-white rounded-[16px] transition-shadow duration-300 ease-out",
    // Same soft lift as PricingCard on hover, just a smaller spread to
    // match the add-on card's lower profile (24px blur vs the pricing
    // card's 32px). Single layer, negative spread keeps the edge clean.
    href &&
      "cursor-pointer hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.08)]",
    className
  )

  if (href) {
    return (
      <Link href={href} className={styles}>
        {content}
      </Link>
    )
  }
  return <div className={styles}>{content}</div>
}
